import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Popper from '@mui/material/Popper';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import { useConfig } from '@rs-core/context/ConfigContext';
import { logInfo } from '@rs-core/utils/logger';
import getCustomFields from '../../../../../../api/getCustomFields';
import getCustomPicklistBookmarks from '../../../../../../utils/getCustomPicklistBookmarks';
import useGetSearchParams from '@rs-core/hooks/useGetSearchParams';
import { useBookmarksStore } from '@worklist-2/worklist/src/DocumentViewerV3/store/bookmarks';

const CustomPicklistOptions = ({ anchorEl, fieldId, value, updateSelected, handleClose }) => {
	const [loadingList, setLoadingList] = useState(false);

	const customPickLists = useBookmarksStore(state => state.customPickLists);
	const setCustomPickLists = useBookmarksStore(state => state.setCustomPickLists);

	const { internalManagingOrganizationID } = useGetSearchParams();
	const { t } = useTranslation('bookmarks');
	const __config = useConfig();

	const isOpen = Boolean(anchorEl);

	useEffect(() => {
		if (!customPickLists?.length && isOpen) {
			const loadList = async () => {
				setLoadingList(true);
				const pickListData = await getCustomFields({
					__config,
					criteria: `?type=picklist&InternalOrganizationID=${internalManagingOrganizationID}`,
				});
				setCustomPickLists(getCustomPicklistBookmarks(pickListData));
				setLoadingList(false);
			};
			loadList();
		}
	}, [isOpen]);

	const ref = useRef(null);

	const getpicklistOptions = () => {
		if (customPickLists) {
			const filter = customPickLists.find(x => x.fieldId === +fieldId);
			if (filter) {
				logInfo('CustomPicklists', `Filter found for fieldId: ${fieldId}`);
				if (filter.options) {
					logInfo('CustomPicklists', `Filter options: ${JSON.stringify(filter.options)}`);
				} else {
					logInfo('CustomPicklists', 'Filter options are absent');
				}
			} else {
				logInfo('CustomPicklists', `No filter found for fieldId: ${fieldId}`);
			}
			logInfo(
				'CustomPicklists',
				JSON.stringify({
					count: customPickLists?.length,
					hasData: !!customPickLists,
					filterCount: filter?.options?.length,
					hasFilters: !!filter?.options,
					filterOptions: filter?.options,
				})
			);
			return filter?.options || [];
		}
		logInfo('CustomPicklists', 'customPickLists is not defined');
		return [];
	};

	const picklistOptions = getpicklistOptions();
	logInfo('CustomPicklists', `picklistOptions fieldId: ${fieldId} length: ${picklistOptions.length}`);

	const containsValue = (valueToCheck, currentValue) => {
		if (typeof currentValue !== 'string' || typeof valueToCheck !== 'string') {
			return;
		}
		const valuesArray = currentValue.split(',').map(item => item.trim());
		return valuesArray.includes(valueToCheck);
	};

	const selectSingleItem = index => {
		const item = picklistOptions?.[index];
		if (!item) return;
		const inputValue = t(item?.item);

		if (typeof inputValue !== 'string') {
			return;
		}

		updateSelected(inputValue);
		handleClose();
	};

	return (
		<Popper ref={ref} disablePortal anchorEl={anchorEl} open={isOpen} sx={{ zIndex: '999' }}>
			<div className="PicklistItems" data-testid="BookmarkItems">
				{loadingList ? (
					<Box
						data-testid="loader"
						sx={{ display: 'grid', placeContent: 'center', height: '100%', minWidth: '180px' }}
					>
						<CircularProgress color="info" size={30} />
					</Box>
				) : picklistOptions?.length ? (
					picklistOptions.map((item, index) => (
						<button
							key={index}
							className={containsValue(item?.item, value) ? 'is-selected' : ''}
							data-testid="bookmark-item"
							id={`PicklistItems${index}`}
							onClick={() => selectSingleItem(index)}
							onMouseDown={e => e.preventDefault()}
						>
							<Box
								sx={{
									opacity: containsValue(item?.item, value) ? 1 : 0,
									marginRight: '8px',

									'& svg': {
										width: '12px',
									},
								}}
							>
								<CheckOutlinedIcon />
							</Box>
							{t(item?.item)}
						</button>
					))
				) : (
					<div className="item" data-testid="no-result">
						No result
					</div>
				)}
			</div>
		</Popper>
	);
};

export default CustomPicklistOptions;
